import { Fragment, type ReactNode } from 'react';
import { learnImageBindings } from './learnImageBindings';

// Restricted authored Markdown: no arbitrary HTML, scripts or embedded application controls.
export function inline(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^\s)]+\))/g).map((part, i) => {
    if (part.startsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link && /^(https:\/\/|\/learn(?:\/|$)|#)/.test(link[2])) return <a key={i} href={link[2]}>{link[1]}</a>;
    return <Fragment key={i}>{part}</Fragment>;
  });
}
export function LearnText({ text }: { text: string }) {
  const lines = text.trim().split('\n');
  const blocks: ReactNode[] = [];
  for (let i = 0; i < lines.length;) {
    const line = lines[i].trim();
    if (!line || /Open full-size/.test(line)) { i++; continue; }
    if (line.startsWith('![')) {
      const topic=line.match(/with-evidence\/([^/]+)\/screen\.jpg/)?.[1];
      const image=learnImageBindings.find(item=>item.slug==='read-assessment-results' && item.topic===topic);
      if (image) blocks.push(<figure key={i}><img src={image.src} width="760" height="871" loading="lazy" alt={image.alt}/><figcaption>{image.caption} <a href={image.src}>Open full-size {image.id} report example</a></figcaption></figure>);
      i++; continue;
    }
    if (line === '<details>') {
      const end = lines.indexOf('</details>', i);
      const summary = lines[i+1]?.match(/^<summary>(.+)<\/summary>$/)?.[1] ?? 'Check your answer';
      const answer = lines.slice(i + 1, end).filter(l => !l.startsWith('<summary>')).join('\n');
      blocks.push(<details key={i}><summary>{summary}</summary><LearnText text={answer} /></details>);
      i = end < 0 ? lines.length : end + 1; continue;
    }
    const heading = line.match(/^### (.+)/);
    if (heading) { blocks.push(<h3 key={i}>{inline(heading[1])}</h3>); i++; continue; }
    if (line.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) rows.push(lines[i++].trim().split('|').slice(1, -1).map(c => c.trim()));
      blocks.push(<div className="learn-table" role="region" aria-label="Assessment steps" tabIndex={0} key={i}><table><thead><tr>{rows[0].map((c,j)=><th key={j} scope="col">{inline(c)}</th>)}</tr></thead><tbody>{rows.slice(2).map((row,j)=><tr key={j}>{row.map((c,k)=><td key={k}>{inline(c)}</td>)}</tr>)}</tbody></table></div>); continue;
    }
    if (/^(- |\d+\. )/.test(line)) {
      const ordered = /^\d/.test(line); const items: string[]=[];
      while (i<lines.length && (ordered ? /^\d+\. / : /^- /).test(lines[i].trim())) items.push(lines[i++].trim().replace(/^(?:- |\d+\. )/,''));
      const children=items.map((item,j)=><li key={j}>{inline(item)}</li>);
      blocks.push(ordered?<ol key={i}>{children}</ol>:<ul key={i}>{children}</ul>); continue;
    }
    let paragraph=line; i++;
    while (i<lines.length && lines[i].trim() && !/^(?:### |<details>|\||- |\d+\. |!\[)/.test(lines[i].trim())) paragraph+=' '+lines[i++].trim();
    blocks.push(<p key={i}>{inline(paragraph)}</p>);
  }
  return <>{blocks}</>;
}